import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { CopyButton } from './CopyButton'
import * as api from '../api/agentTokens'
import './AgentTokensPanel.css'

export function AgentTokensPanel() {
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [scope, setScope] = useState<api.Scope>('read_only')
  const [ttl, setTtl] = useState(3600) // default 1h
  const [issued, setIssued] = useState<api.CreatedToken | null>(null)

  const tokens = useQuery({ queryKey: ['agent-tokens'], queryFn: api.listTokens })
  const create = useMutation({
    mutationFn: () => api.createToken({ label, scope, ttl_seconds: ttl }),
    onSuccess: (t) => {
      setIssued(t)
      setLabel('')
      qc.invalidateQueries({ queryKey: ['agent-tokens'] })
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeToken(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-tokens'] }),
  })

  return (
    <section className="agent-tokens">
      <Link to="/" className="at-back">
        <ArrowLeft size={14} aria-hidden="true" />
        Back to dashboard
      </Link>
      <h2>Agent Tokens</h2>
      <p className="at-intro">
        Generate revocable API tokens for agents and scripts. Each token is shown
        once at creation — copy it immediately. Use it as the <code>X-API-Key</code>{' '}
        header.
      </p>

      <form className="at-form" onSubmit={(e) => { e.preventDefault(); create.mutate() }}>
        <input
          placeholder="Label (e.g. monitoring-bot)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
        />
        <select value={scope} onChange={(e) => setScope(e.target.value as api.Scope)}>
          <option value="read_only">Read-only</option>
          <option value="full">Full access</option>
        </select>
        <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
          {api.EXPIRY_OPTIONS.map((o) => (
            <option key={o.seconds} value={o.seconds}>{o.label}</option>
          ))}
        </select>
        <button type="submit" disabled={create.isPending}>Generate</button>
      </form>

      {create.isError && (
        <div className="at-error" role="alert">
          Failed to create token. Please try again.
        </div>
      )}

      {issued && (
        <div className="at-issued" role="alert">
          <strong>Save this token now — it won&apos;t be shown again.</strong>
          <div className="at-token">
            <code>{issued.token}</code>
            <CopyButton value={issued.token} />
          </div>
        </div>
      )}

      <table className="at-table">
        <thead>
          <tr>
            <th>Label</th><th>Scope</th><th>Created</th><th>Expires</th><th>Last used</th><th></th>
          </tr>
        </thead>
        <tbody>
          {(tokens.data ?? []).map((t) => (
            <tr key={t.id}>
              <td>{t.label}</td>
              <td>{t.scope}</td>
              <td>{new Date(t.created_at).toLocaleString()}</td>
              <td>{new Date(t.expires_at).toLocaleString()}</td>
              <td>{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : '—'}</td>
              <td>
                <button
                  className="at-revoke"
                  onClick={() => {
                    if (confirm(`Revoke "${t.label}"?`)) revoke.mutate(t.id)
                  }}
                >
                  Revoke
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!tokens.isLoading && (tokens.data ?? []).length === 0 && (
        <p className="at-empty">No active tokens.</p>
      )}
    </section>
  )
}
